import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useAccountSync } from "./useAccountSync";
import type { BootstrapStep } from "../types/auth.types";
import type { ConsentDocument } from "../types/consent.types";

/**
 * Runs the backend handshake once per Clerk session, from wherever the app
 * first renders as signed in, and reports where it has got to so the caller can
 * hold the app back until it is done.
 *
 * It lives here rather than on the screens that sign the user in because those
 * screens cannot be relied on to still be running. Clerk finishes a social
 * sign-in with a top-level redirect - the `__clerk_handshake` bounce through
 * its Frontend API - which lands the browser on the destination as a fresh
 * page load, so `/sso-callback` never gets to navigate and anything it had
 * queued is gone. The same is true of a refresh, a bookmarked deep link, or a
 * session restored from its cookie. All of those end up here.
 *
 * "Once per session" is remembered in `localStorage` against the Clerk session
 * id, which is what keeps a reload from repeating the workspace POST. Signing
 * out and back in mints a new session id, so the next sign-in syncs again.
 */

/**
 * `consent` is the one phase the handshake does not leave on its own: it is
 * waiting on the user, not on the network.
 */
export type BootstrapPhase = "syncing" | "consent" | "ready" | "failed";

export interface SessionBootstrap {
  phase: BootstrapPhase;
  /** Which call is in flight. Only meaningful while `syncing`. */
  step: BootstrapStep;
  /** Why it failed. Only set when `failed`. */
  message: string;
  /** What the user is being asked to agree to. Only set when `consent`. */
  documents: ConsentDocument[];
  /** Releases the handshake once the agreement has been recorded. Only
   *  meaningful when `consent`. */
  consentRecorded: () => void;
  /** Runs the handshake again, for the retry on the failure screen. */
  retry: () => void;
}

/** Single key, holding the last session id that completed a sync. */
const SYNCED_SESSION_KEY = "naurist:synced-session";

/**
 * Mirror of the stored value, read from storage once per page load.
 *
 * The cache is what lets an already-synced session be recognised during render
 * rather than in an effect: without it every reload would paint the loading
 * screen for a frame before the effect could say it was not needed.
 */
let syncedSession: string | null | undefined;

// Storage can throw outright - Safari private mode, or a browser with site
// data blocked. A session that cannot be remembered syncs again next load,
// which is a far better failure than the app not rendering.
const readSyncedSession = (): string | null => {
  if (syncedSession === undefined) {
    try {
      syncedSession = window.localStorage.getItem(SYNCED_SESSION_KEY);
    } catch {
      syncedSession = null;
    }
  }
  return syncedSession;
};

const rememberSyncedSession = (sessionId: string): void => {
  syncedSession = sessionId;
  try {
    window.localStorage.setItem(SYNCED_SESSION_KEY, sessionId);
  } catch {
    /* not fatal - see above */
  }
};

export function useSessionBootstrap(): SessionBootstrap {
  const { isLoaded, isSignedIn, sessionId } = useAuth();
  const syncAccount = useAccountSync();

  const [state, setState] = useState<{
    phase: BootstrapPhase;
    step: BootstrapStep;
    message: string;
    documents: ConsentDocument[];
  }>({ phase: "syncing", step: "account", message: "", documents: [] });

  /**
   * How the paused handshake is let go of again.
   *
   * The consent step awaits a promise that only the screen can settle, so the
   * resolver is parked here between the two - which is what turns "the user has
   * to answer this" into an ordinary `await` inside the sync rather than the
   * sequence having to be broken into stages around it.
   */
  const releaseConsent = useRef<(() => void) | null>(null);

  const requireConsent = useCallback(
    (documents: ConsentDocument[]) =>
      new Promise<void>((resolve) => {
        releaseConsent.current = resolve;
        setState((current) => ({ ...current, phase: "consent", documents }));
      }),
    []
  );

  const consentRecorded = useCallback(() => {
    const resolve = releaseConsent.current;
    releaseConsent.current = null;
    setState((current) => ({ ...current, phase: "syncing", documents: [] }));
    resolve?.();
  }, []);

  // Bumped by `retry`, which is the only thing that runs the handshake again
  // for a session it has already attempted.
  const [attempt, setAttempt] = useState(0);

  /**
   * What the effect has already run for.
   *
   * The effect depends on `syncAccount`, whose identity follows Clerk's
   * `getToken` and is not guaranteed to be stable across renders - so it can
   * re-fire at any time. Keying on the attempt rather than on an in-flight flag
   * is what makes that harmless: a flag only covers the window while the calls
   * are running, and would let a re-render after a failure start them over
   * behind the retry screen.
   */
  const attempted = useRef<string | null>(null);
  const attemptKey = `${sessionId ?? ""}#${attempt}`;

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !sessionId) return;
    if (readSyncedSession() === sessionId) return;
    if (attempted.current === attemptKey) return;

    attempted.current = attemptKey;
    setState({
      phase: "syncing",
      step: "account",
      message: "",
      documents: [],
    });

    void syncAccount({
      onStep: (step) => setState((current) => ({ ...current, step })),
      onConsentRequired: requireConsent,
    }).then((outcome) => {
      if (outcome.ok) {
        // Only a session the backend actually answered for is marked. An
        // unreachable server is retried on the next load rather than being
        // written off until the user signs in again.
        //
        // Consent that could not be recorded counts as unanswered, for the same
        // reason and with more at stake: the agreement the user gave on the way
        // in is parked in this tab and nowhere else, so a session marked done
        // here would never run the POST again and the next sign-in would put
        // the same documents back in front of them. Left unmarked, the very
        // next load retries it while the record is still there.
        if (outcome.consentSettled) {
          rememberSyncedSession(sessionId);
        }
        setState({
          phase: "ready",
          step: "workspace",
          message: "",
          documents: [],
        });
      } else {
        setState({
          phase: "failed",
          step: "account",
          message: outcome.message,
          documents: [],
        });
      }
    });
  }, [
    isLoaded,
    isSignedIn,
    sessionId,
    syncAccount,
    attemptKey,
    requireConsent,
  ]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  // Derived rather than stored, so a session synced earlier in this page's life
  // - or on a previous load - is ready on the very first render.
  const alreadySynced =
    isLoaded && isSignedIn && !!sessionId && readSyncedSession() === sessionId;

  return {
    phase: alreadySynced ? "ready" : state.phase,
    step: state.step,
    message: state.message,
    documents: state.documents,
    consentRecorded,
    retry,
  };
}
